<template>
  <layout>
    <page-header :actions="actions" :header="$t('orders.title')" />
    <div class="content">
      <admin-table v-bind="tableProps">
        <template slot="user" slot-scope="{ item }">
          <div v-if="item.user">
            {{ item.user.name }}
          </div>
          <div v-else>
            <system-user />
          </div>
        </template>
        <template slot="recipient" slot-scope="{ item }">
          <div v-if="item.recipient">
            <i v-if="item.recipient.company_id" class="icon-office"></i>
            {{ item.recipient.name }}
          </div>
          <div v-else>
            <system-user />
          </div>
        </template>
        <template slot="amount" slot-scope="{ item }">
          <div>
            {{ item.amount | currency(item.currency_label) }}
          </div>
          <b-badge :variant="statusVariant(item.status)">
            {{ item.status_label }}
          </b-badge>
          <value-payment-method :item="item" />
        </template>
      </admin-table>
    </div>
  </layout>
</template>

<script>
import { AdminTable } from 'stylemix-vue-admin'
import { create } from 'vue-modal-dialogs'
import CompanyOrdersApi from './CompanyOrdersApi'
import SystemUser from '../components/SystemUser'
import View from '../orders/View'
import ValuePaymentMethod from '../components/values/ValuePaymentMethod'

const resourceBase = 'orders'

export default {
  name: 'Orders',

  components: {
    ValuePaymentMethod,
    AdminTable,
    SystemUser,
  },

  data() {
    return {
      // Page header actions
      actions: [],

      // Table resource configuration
      tableProps: {
        base: resourceBase,
        apiBuilder() {
          return new CompanyOrdersApi().setParameters({
            include: 'user,recipient',
          })
        },
        sortBy: 'created_at',
        sortDesc: true,
        perPage: 15,
        columns: [
          {
            key: 'invoice_number',
            label: this.$t('orders.number'),
            formatter: value => `#${value}`,
            sortable: true,
          },
          {
            key: 'description',
            label: this.$t('orders.description'),
          },
          {
            key: 'user',
            label: this.$t('orders.payee'),
          },
          {
            key: 'recipient',
            label: this.$t('orders.recipient'),
          },
          {
            key: 'amount',
            label: this.$t('orders.amount'),
            sortable: true,
          },
          {
            key: 'completed_at',
            label: this.$t('orders.completed'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
            },
          },
          {
            key: 'created_at',
            label: this.$t('orders.created'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
            },
          },
          // define column fields here
        ],

        // override search fields here using fields schema
        searchFields: [
          {
            component: 'select-field',
            attribute: 'where.status',
            placeholder: 'Order status',
            options: [
              { value: null, label: this.$t('orders.all_statuses') },
              { value: 'completed', label: this.$t('orders.completed') },
              { value: 'pending,partial', label: this.$t('orders.unpaid') },
              { value: 'cancelled', label: this.$t('orders.cancelled') },
            ],
          },
          {
            component: 'text-field',
            type: 'search',
            attribute: 'where.query',
            placeholder: 'Search ...',
          },
          {
            component: 'date-range-field',
            attribute: 'where.created_at',
            placeholder: 'Creation date period',
          },
        ],

        // define custom row actions
        rowActions: item => {
          const actions = [
            {
              label: this.$t('general.view'),
              variant: 'light',
              handler: () => {
                this.viewDialog(item)
              },
            },
          ]

          if (
            item.status !== 'completed' &&
            item.amount > 0 &&
            item.user &&
            item.user.id === this.$account.id
          ) {
            actions.push({
              label: this.$t('general.pay'),
              variant: 'success',
              handler: () => {
                this.$router.push({
                  name: 'company-orders-pay',
                  params: {
                    id: item.id,
                  },
                })
              },
            })
          }

          return actions
        },

        // define custom bulk actions
        bulkActions() {
          return []
        },
      },
    }
  },

  created() {
    this.viewDialog = create(View, 'order')
  },

  methods: {
    statusVariant(status) {
      switch (status) {
        case 'pending':
          return 'warning'
        case 'completed':
          return 'success'
        case 'cancelled':
          return 'light'
      }

      return 'info'
    },
  },
}
</script>
